import { getFormattedDate } from 'src/utils/date-formatter.util'
import { useSession } from 'src/providers/auth.provider'
import {
  CurecallBadge,
  CurecallButton,
  CurecallDropdownButton,
  CurecallDropdownItemStyle,
  CurecallTag,
  ECurecallBadgeColor,
  ECurecallSize,
  PenIcon,
} from '@honestica/curecall-ui'
import { TPatientListTab } from 'src/types/patient-list'
import { TUser, EUserRole } from 'src/types/user'
import { useSearchParams } from 'react-router-dom'
import { TimezoneAwareTime } from 'src/components/timezone-aware-time/timezone-aware-time.component'
import { twMerge } from 'src/utils/tw-merge.util'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { LocalizedLink } from 'src/components/navigation/localized-link.component'
import { AdminOnlyBadge } from 'src/components/admin-only-badge.component'
import dayjs from 'dayjs'
import { useCurrentProject, WithPermission } from 'src/providers/current-project.provider'
import { TPatient } from 'src/types/patient'
import { TPatientMedEvent } from 'src/types/med-event'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
} from '../../../configuration/permissions/_components/permissions.type'
import { TPatientFollowup } from 'src/types/followup'
import { TPatientWorkflow } from 'src/types/patient-workflow'
import { TPatientForm } from 'src/types/form'
import { PatientPhone } from 'src/components/patient/patient-phone.component'
import { getDoctorCivilityAndName } from 'src/utils/string-formatter.util'
import { useModals } from 'src/providers/modals.provider'

const CommentCell = ({ comment }: { comment: { value: string; updatedAt: string } }) => (
  <div>
    <span style={{ fontSize: '1.3em' }}>&quot;{comment.value}&quot;</span> -{' '}
    {getFormattedDate(comment.updatedAt)}
  </div>
)

const IdCell = ({
  id,
  isTestAccount,
  isDisabled = false,
}: {
  id?: number | string
  isTestAccount: boolean
  isDisabled?: boolean
}) => {
  return (
    <div className={twMerge('c-flex c-items-center', isDisabled && 'c-text-grey-500')}>
      {isTestAccount && <div className="rounded-badge">T</div>}
      <div className="c-font-bold">{id}</div>
    </div>
  )
}

const PatientNamingCell = ({ completeName }: { completeName: string }) => (
  <div className="flex items-center">
    <div style={{ marginLeft: '1rem' }}>{completeName}</div>
  </div>
)

const IdentityCell = ({ patient, projectId }: { patient: TPatient; projectId: number }) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const oldParams = new URLSearchParams(searchParams.toString())

  return (
    <div>
      <LocalizedLink
        to={`/${projectId}/medical/patients/${patient.id}?${oldParams}`}
        className={twMerge(
          'c-font-bold identity-cell hover:c-underline',
          (patient.isArchived || patient?.PatientConsent?.consentStatus === false) && 'text-muted',
        )}
      >
        <p className="c-min-w-fit c-w-max c-my-0">
          {patient.lastname?.toUpperCase()} {patient.firstname}{' '}
          {patient.gender !== 'UNKNOWN' ? `(${patient.gender.substring(0, 1)})` : ``}
        </p>
      </LocalizedLink>
      {patient.birthdate && (
        <div>
          {t('pages.patient-list-page.born-on')} {getFormattedDate(patient.birthdate)}
        </div>
      )}
      <PatientPhone patient={patient} />
    </div>
  )
}

const MedEventCell = ({
  patientMedEvent,
  isDisabled = false,
}: {
  patientMedEvent?: TPatientMedEvent
  isDisabled?: boolean
}) => {
  const { t } = useTranslation()
  const { getMedEventLabelById } = useCurrentProject()

  if (!patientMedEvent) return null

  const medEventOriginalLabel = patientMedEvent?.metadata?.originalLabel

  return (
    <div className={twMerge('c-min-w-[170px] c-flex c-flex-col c-gap-1', isDisabled && 'c-text-grey-500')}>
      <div>
        <div>{getMedEventLabelById(patientMedEvent.MedEventId)}</div>
        {medEventOriginalLabel && <div>{medEventOriginalLabel}</div>}
        <div className="font-italic">
          <TimezoneAwareTime date={patientMedEvent.date} formatter="dateWithHours" />
        </div>
        <div>{getDoctorCivilityAndName(patientMedEvent?.MedicalStaffMember, false)}</div>
      </div>
      {patientMedEvent.status === 'REVIEWED' && (
        <CurecallBadge color={ECurecallBadgeColor.GREEN} className="c-w-min">
          {t('components.patient-med-event-overview.reviewed')}
        </CurecallBadge>
      )}
    </div>
  )
}

const ConvocationDateCell = ({
  patientId,
  patientMedEvent,
}: {
  patientId: number
  patientMedEvent?: TPatientMedEvent
}) => {
  const { openMedEventModal } = useModals()

  if (!patientMedEvent?.convocationDate) return null
  return (
    <p className="c-flex c-items-center">
      <TimezoneAwareTime date={patientMedEvent.convocationDate} formatter="dateWithHours" />{' '}
      <WithPermission
        requiredSubject={EPermissionGrantSubjects.PATIENT}
        requiredAction={EPermissionGrantActions.UPDATE}
      >
        <CurecallButton
          iconType={<PenIcon />}
          isGhostButton={true}
          ariaLabelText="todo"
          onClick={() => {
            openMedEventModal({ patientId, patientMedEventForForm: patientMedEvent })
          }}
        />
      </WithPermission>
    </p>
  )
}

const WorkflowCell = ({
  PatientWorkflows,
  isDisabled = false,
}: {
  PatientWorkflows: TPatientWorkflow[]
  isDisabled?: boolean
}) => {
  const workflow = PatientWorkflows[0]?.Workflow

  if (!workflow) return null

  const { label } = workflow

  return (
    <div className={twMerge(isDisabled && 'c-text-grey-500')}>
      <h6>
        <CurecallTag isWorkflow size={ECurecallSize.SMALL} isDisabled={isDisabled}>
          {label}
        </CurecallTag>
      </h6>
      <div className="font-italic">
        <TimezoneAwareTime date={PatientWorkflows[0]?.updatedAt} formatter="dateWithHours" />
      </div>
    </div>
  )
}

const DoctorNamingCell = ({
  title,
  firstname,
  lastname,
}: Pick<TUser, 'title' | 'firstname' | 'lastname'>) => (
  <div className="flex items-center">
    <i className="iconsminds-medical-sign c-text-2xl" />
    <div style={{ marginLeft: '1rem' }}>
      {title} {firstname} {lastname}
    </div>
  </div>
)

const DoctorCell = ({
  patient,
  selectedService,
}: {
  patient: TPatient
  selectedService?: TPatientListTab
}) => {
  const defaultDoctorFromFirstPatientFollowup =
    patient?.PatientFollowups && patient?.PatientFollowups[0]?.MedicalStaffMember
  const doctor =
    selectedService?.name === 'Chirurgie Ambulatoire'
      ? patient?.PatientMedEvents[0]?.MedicalStaffMember ||
        defaultDoctorFromFirstPatientFollowup ||
        patient?.MedicalStaffMember
      : patient.MedicalStaffMember ||
        patient?.PatientMedEvents[0]?.MedicalStaffMember ||
        defaultDoctorFromFirstPatientFollowup

  return (
    <>
      {doctor && (
        <div className={twMerge('c-font-bold', patient.isArchived && 'c-text-grey-400')}>
          {getDoctorCivilityAndName(doctor, false)}
          {doctor.activitySectorLabel && <div>{doctor.activitySectorLabel}</div>}
        </div>
      )}
    </>
  )
}

const PatientFollowupCell = ({ PatientFollowups = [] }: { PatientFollowups?: TPatientFollowup[] }) => {
  // This cell seems to be not used in BDD, no one seems to use it so we should remove it
  // After few days if sentry does not log this, we can safelly remote this code
  Sentry.captureMessage(`PatientFollowupCell is used for ${JSON.stringify(PatientFollowups)}`)
  return null
}

const ActiveFollowups = ({
  patientFollowups = [],
  className = '',
  isDisabled = false,
}: {
  patientFollowups?: TPatientFollowup[]
  className?: string
  isDisabled?: boolean
}) => {
  const { t } = useTranslation()
  return (
    <div className={twMerge(className, isDisabled && 'c-text-grey-500')}>
      {patientFollowups?.map((patientFollowup) => (
        <div key={patientFollowup.id}>
          {t('pages.patient-list-page.followup-to-date', {
            followupName: patientFollowup.Followup?.name,
            date: getFormattedDate(patientFollowup?.endAt),
          })}
        </div>
      ))}
    </div>
  )
}

const TestCell = ({ patientForms }: { patientForms?: TPatientForm[] }) => {
  // This cell seems to be not used in BDD, no one seems to use it so we should remove it
  // After few days if sentry does not log this, we can safelly remote this code
  Sentry.captureMessage(`PatientForms & TestCell are used for ${JSON.stringify(patientForms)}`)
  return null
}

const DateIntervalCell = ({ patientFollowups }: { patientFollowups?: TPatientFollowup[] }) => {
  // This cell seems to be not used in BDD, no one seems to use it so we should remove it
  // After few days if sentry does not log this, we can safelly remote this code
  Sentry.captureMessage(
    `PatientFollowUpPeriod & DateIntervalCell are used for ${JSON.stringify(patientFollowups)}`,
  )
  return null
}

const DateCell = ({
  patientFollowups = [],
  isPeriodicWorkflow = false,
  isDisabled = false,
}: {
  patientFollowups?: TPatientFollowup[]
  isPeriodicWorkflow?: boolean
  isDisabled?: boolean
}) => {
  const getFirstPatientFollowupStartDate = (patientFollowups: TPatientFollowup[]) => {
    const firstPatientFollowup =
      patientFollowups.length > 1
        ? patientFollowups.sort(
            (followup1: TPatientFollowup, followup2: TPatientFollowup) =>
              new Date(followup2?.startDate)?.getTime() - new Date(followup1?.startDate)?.getTime(),
          )?.[0]
        : patientFollowups[0]
    if (!firstPatientFollowup) return ''

    return isPeriodicWorkflow
      ? getFormattedDate(dayjs(firstPatientFollowup.startDate).add(15, 'day').toDate())
      : getFormattedDate(new Date(firstPatientFollowup.startDate))
  }
  const date = getFirstPatientFollowupStartDate(patientFollowups)
  return (
    <div className={twMerge(isDisabled && 'c-text-grey-500')}>
      {date && (
        <>
          <div>{`${date}`}</div>
        </>
      )}
    </div>
  )
}

const ActionCell = ({ patient, projectId }: { patient: TPatient; projectId: number }) => {
  const { t } = useTranslation()
  const { currentUser } = useSession()
  const { project, hasPermission } = useCurrentProject()
  const {
    openFollowupModal,
    openConsentModal,
    openMedicalTeamCommentsModal,
    openArchiveModal,
    openDeleteTestPatientModal,
  } = useModals()

  const { consentNeeded } = project

  const actionButtonItems = []

  const userCanUpdatePatient = hasPermission(EPermissionGrantSubjects.PATIENT, EPermissionGrantActions.UPDATE)

  const ButtonItem = ({
    name,
    onClick,
    isAdminOnly,
  }: {
    name: string
    onClick: VoidFunction
    isAdminOnly?: boolean
  }) => {
    return (
      <li>
        <button
          type="button"
          className={twMerge(CurecallDropdownItemStyle, 'c-text-start hover:c-text-grey-700')}
          onClick={onClick}
        >
          {isAdminOnly ? (
            <span className="c-flex c-items-center c-gap-1">
              <AdminOnlyBadge />
              {name}
            </span>
          ) : (
            name
          )}
        </button>
      </li>
    )
  }

  if (userCanUpdatePatient && !patient.isArchived) {
    patient?.PatientFollowups?.map((patientFollowup) => {
      actionButtonItems.push(
        <ButtonItem
          name={t('pages.patient-list-page.schedule-appointments-for-followup-name', {
            followupName: patientFollowup.Followup?.name,
          })}
          key={`action-button-patient-followup-${patientFollowup.id}-${patient.id}`}
          onClick={() => {
            openFollowupModal({
              patientId: patient.id,
              patientFollowupIdToEdit: patientFollowup.id,
            })
          }}
        />,
      )
    })
  }

  if (
    userCanUpdatePatient &&
    consentNeeded &&
    !patient?.PatientConsent?.consentStatus &&
    !patient.isArchived
  ) {
    actionButtonItems.push(
      <ButtonItem
        name={t('pages.patient-list-page.obtain-patient-content')}
        key={`action-button-consent-${patient.id}`}
        onClick={() => {
          openConsentModal({ patientId: patient.id })
        }}
      />,
    )
  }

  // This is a work-around while new Planning is ready
  const correspondingPatientFollowup = patient.PatientFollowups?.find(
    (pf) =>
      pf.id === patient?.lastPatientMedEvent?.PatientFollowupId ||
      pf.id === patient?.nextPatientMedEvent?.PatientFollowupId,
  )
  if (!patient.isArchived && correspondingPatientFollowup) {
    actionButtonItems.push(
      <ButtonItem
        name={t('pages.patient-list-page.medical-team-comments')}
        key={`action-button-patient-comments-${patient.id}`}
        onClick={() => {
          openMedicalTeamCommentsModal({
            patientId: patient.id,
            patientFollowup: correspondingPatientFollowup,
          })
        }}
      />,
    )
    actionButtonItems.push(
      <li key={`action-button-patient-see-file-${patient.id}`}>
        <LocalizedLink
          to={`/${projectId}/medical/patients/${patient.id}`}
          className={twMerge(CurecallDropdownItemStyle, 'c-text-start hover:c-text-grey-700')}
        >
          {t('pages.patient-list-page.see-file')}
        </LocalizedLink>
      </li>,
    )
  }

  if (patient.isTestAccount && ['sales', 'admin'].includes(currentUser?.role as EUserRole)) {
    actionButtonItems.push(
      <ButtonItem
        name={t('pages.patient-list-page.delete-test-patient')}
        key={`action-button-patient-is-test-account-${patient.id}`}
        onClick={() => openDeleteTestPatientModal({ patient })}
        isAdminOnly
      />,
    )
  }

  if (!patient.isTestAccount && userCanUpdatePatient) {
    actionButtonItems.push(
      <ButtonItem
        name={
          patient.isArchived
            ? t('components.patient.archive-or-delete.unarchived-this-account')
            : t('components.patient.archive-or-delete.archived-this-patient')
        }
        key={`action-button-patient-archive-${patient.id}`}
        onClick={() => openArchiveModal({ patient })}
      />,
    )
  }

  if (!actionButtonItems.length) return null

  return (
    <CurecallDropdownButton
      dropdownButtonLabel={t('pages.patient-list-page.action')}
      size={ECurecallSize.SMALL}
      customItems={actionButtonItems}
    />
  )
}

const PatientCommentsCell = ({
  hasComment,
  patient,
  isDisabled = false,
}: {
  hasComment: boolean
  patient: TPatient
  isDisabled?: boolean
}) => {
  const { t } = useTranslation()
  const { openMedicalTeamCommentsModal } = useModals()
  const correspondingPatientFollowup = patient.PatientFollowups?.find(
    (pf) =>
      pf.id === patient.lastPatientMedEvent?.PatientFollowupId ||
      pf.id === patient?.nextPatientMedEvent?.PatientFollowupId,
  )
  return (
    <>
      <img
        src={`/icons/${hasComment ? 'display-event-comment.svg' : 'disabled-comment.svg'}`}
        alt={
          hasComment
            ? t('pages.patient-list-page.display-comments-icon')
            : t('pages.patient-list-page.disabled-comments-icon')
        }
        data-testid="remove-icon"
        onClick={() =>
          !isDisabled &&
          correspondingPatientFollowup &&
          openMedicalTeamCommentsModal({
            patientId: patient.id,
            patientFollowup: correspondingPatientFollowup,
          })
        }
        width={20}
        height={20}
        className="c-cursor-pointer"
        style={{
          maxWidth: '100%',
          height: 'auto',
        }}
      />
    </>
  )
}

export {
  ActionCell,
  CommentCell,
  DoctorNamingCell,
  IdentityCell,
  PatientNamingCell,
  IdCell,
  MedEventCell,
  WorkflowCell,
  DoctorCell,
  PatientFollowupCell,
  TestCell,
  DateIntervalCell,
  DateCell,
  ActiveFollowups,
  PatientCommentsCell,
  ConvocationDateCell,
}
